import {
  buildTranslateGeometrySnapshot,
  revertTranslateGeometrySnapshot
} from '../utils/panelMapTranslateCancelSnapshot.js';
import { findTreeItemById } from '../utils/mapHierarchySelectionContext.js';
import { resolveModuleTreeItemForMapFeature } from '../utils/panelPlanningBookmark.js';
import { readStringSeq } from '../utils/panelMapFeatureProps.js';

/**
 * 취소·중단 시 맵 UI를 동작 시작 전(선택·하이라이트·interaction)으로 복원
 */
export const mapInteractionCancelSetup = {
  data() {
    return {
      /** @type {Array<{ feature: import('ol/Feature').default, geometry: import('ol/geom/Geometry').default }>|null} */
      _translateCancelSnapshot: null,
      /** @type {{ cancelCtrlDragCopy?: () => void }|null} */
      _mapInteractionCancelHandlers: null
    };
  },

  methods: {
    registerMapInteractionCancelHandlers(handlers = {}) {
      this._mapInteractionCancelHandlers = {
        ...this._mapInteractionCancelHandlers,
        ...handlers
      };
    },

    beginTranslateCancelSnapshot(translateEvent) {
      const movedFeatures = translateEvent?.features?.getArray?.() ?? [];
      if (!movedFeatures.length || !this.vectorSource) {
        this._translateCancelSnapshot = null;
        return;
      }
      this._translateCancelSnapshot = buildTranslateGeometrySnapshot(
        movedFeatures,
        this.vectorSource.getFeatures(),
        readStringSeq
      );
    },

    clearTranslateCancelSnapshot() {
      this._translateCancelSnapshot = null;
    },

    cancelActiveTranslate() {
      const snapshot = this._translateCancelSnapshot;
      if (!snapshot?.length) {
        return false;
      }
      revertTranslateGeometrySnapshot(snapshot);
      this._translateCancelSnapshot = null;
      this.stringInitialPositions = null;
      this.syncSelectionHighlightFromCurrentSelection?.();
      this.requestVectorStyleRefresh?.();
      this.map?.render?.();
      return true;
    },

    /** Ctrl+드래그·붙여넣기·이동 취소 후 Idle 복귀 */
    cancelActiveMapInteractions() {
      this._mapInteractionCancelHandlers?.cancelCtrlDragCopy?.();
      if (this.isPasteMode) {
        this.deactivatePasteMode();
      } else {
        this.clearStringPlacementPreview?.();
      }
      this.cancelActiveTranslate();
      this.ctrlKeyPressed = false;
      if (this._mapCtrlDragActive) {
        this._mapCtrlDragActive = false;
      }
      this.restoreMainMapInteractionsAfterCtrlDrag?.();
      this.dragPanInteraction?.setActive?.(true);
      this.syncSelectionHighlightFromCurrentSelection?.();
    },

    /** 선택·하이라이트를 현재 collection 기준으로 다시 맞춤 */
    syncSelectionHighlightFromCurrentSelection() {
      if (this._mapCtrlDragActive || this.isPasteMode) {
        return;
      }

      const groupCount = this.groupSelection?.getLength?.() ?? 0;
      const moduleCount = this.moduleSelection?.getLength?.() ?? 0;

      if (groupCount === 0 && moduleCount === 0) {
        this.removeSelectionHighlight?.();
        this.requestVectorStyleRefresh?.();
        return;
      }

      let treeItem = null;
      if (this.lastHighlightedItemId && this.treeData) {
        treeItem = findTreeItemById(this.treeData, this.lastHighlightedItemId);
      }
      if (!treeItem && groupCount > 0) {
        treeItem = this.findTreeItemByMapFeature?.(this.groupSelection.getArray()[0]);
      }
      if (!treeItem && moduleCount > 0) {
        treeItem = resolveModuleTreeItemForMapFeature(
          this.moduleSelection.getArray()[0],
          this.treeData
        );
      }

      if (!treeItem) {
        this.requestVectorStyleRefresh?.();
        return;
      }

      if (treeItem.type === 'module' || (moduleCount > 0 && groupCount === 0)) {
        this.removeSelectionHighlight?.();
        const moduleFeatures = this.moduleSelection?.getArray?.() ?? [];
        if (treeItem && moduleFeatures.length) {
          this.syncPanelHierarchyBookmarkFromTreeItem?.(treeItem, { moduleFeatures });
        }
        this.requestVectorStyleRefresh?.();
        return;
      }

      const { groupFeatures } = this.getMapFeaturesForTreeItem?.(treeItem) ?? {};
      const anchors =
        treeItem.type === 'array'
          ? this.expandSparseSelectionForHighlight?.(
              groupFeatures?.length ? groupFeatures : this.groupSelection.getArray()
            )
          : groupFeatures?.length
            ? groupFeatures
            : this.groupSelection.getArray();

      if (anchors?.length) {
        this.highlightRegionWithLabel?.(anchors, treeItem, { skipPan: true });
      } else {
        this.requestVectorStyleRefresh?.();
      }
    },

    restoreMapUiAfterInteractionCancel() {
      this.clearStringPlacementPreview?.();
      this.ctrlKeyPressed = false;
      this._mapCtrlDragActive = false;
      this.restoreMainMapInteractionsAfterCtrlDrag?.();
      this.dragPanInteraction?.setActive?.(true);

      const mapEl = document.getElementById('map');
      if (mapEl && !this.isPasteMode && !this.drawingToolbar?.activeTool) {
        mapEl.style.cursor = '';
      }

      this.syncSelectionHighlightFromCurrentSelection?.();
    }
  }
};
